import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Button,
  Stack,
  Backdrop,
  CircularProgress
} from '@material-ui/core';
import { TrendingUp, TrendingDown, Refresh } from '@material-ui/icons';
import Chart from 'react-apexcharts';
import { areaOptions, BRAND_GOLD } from '../chartDefaults';
import { loansUrl, npvLedgers } from '../../../../utils/constants';

export const currencies = [
  { refId: 'ZWD', name: 'ZWD' },
  { refId: 'USD', name: 'USD' }
];

const formatUSD = (v) =>
  `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const LoansZwd = ({ metric }) => {
  const [ledger, setLedger] = useState('Interest');
  const [currency, setCurrency] = useState('USD');
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dataList, setDataList] = useState([
    {
      glMonth: 'April 2025',
      currency: 'USD',
      totalAmount: 0.0,
      resultDate: '2025-04-01T00:00:00'
    },
    {
      glMonth: 'May 2025',
      currency: 'USD',
      totalAmount: 0.0,
      resultDate: '2025-05-01T00:00:00'
    },
    {
      glMonth: 'June 2025',
      currency: 'USD',
      totalAmount: 0.0,
      resultDate: '2025-06-01T00:00:00'
    }
  ]);

  useEffect(() => {
    getChartData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categories = dataList.map((d) => d.glMonth);
  const series = [{ name: 'Total', data: dataList.map((d) => Number(d.totalAmount.toFixed(2))) }];
  const totalAmount = dataList.reduce((sum, d) => sum + d.totalAmount, 0);
  const options = areaOptions({
    categories,
    color: BRAND_GOLD,
    yFormatter: (v) => formatUSD(v)
  });

  const Icon = metric?.icon;
  const isUp = metric?.trend === 'up';

  const handleRefresh = () => getChartData();

  const getChartData = () => {
    setLoading(true);

    const url = `${loansUrl}/api/warehouse/GetMonthlyNpvTotals?currency=${currency}&ledger=${ledger}`;

    fetch(url)
      .then((res) => res.json())
      .then(
        (result) => {
          setLoading(false);
          console.log('returned GetMonthlyNpvTotals Summary list', result.result);
          setDataList(result.result ?? []); // fallback to empty array
        },
        (error) => {
          setLoading(false);
          // notifyPopup(`Error in getChartData!: ${error}`, 'error');
        }
      );
  };

  const handleLoadingClose = () => {
    setLoading(false);
  };

  return (
    <Card elevation={2}>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
        onClick={handleLoadingClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <CardContent sx={{ p: { xs: 2, md: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            {Icon && <Icon sx={{ color: 'primary.contrastText', fontSize: 32 }} />}
          </Box>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {metric?.title}
            </Typography>
            <Typography variant="h4" color="text.primary" sx={{ fontWeight: 700 }}>
              {formatUSD(totalAmount)}
            </Typography>
          </Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ ml: { sm: 'auto' } }}
          >
            {metric?.trendValue && (
              <Chip
                icon={isUp ? <TrendingUp fontSize="small" /> : <TrendingDown fontSize="small" />}
                label={metric.trendValue}
                size="small"
                sx={{
                  fontWeight: 700,
                  bgcolor: isUp ? 'success.main' : 'error.main',
                  color: '#fff',
                  '& .MuiChip-icon': { color: '#fff' },
                  alignSelf: { xs: 'flex-start', sm: 'center' }
                }}
              />
            )}
            <TextField
              select
              size="small"
              label="NPV Ledger"
              value={ledger}
              onChange={(e) => setLedger(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              {npvLedgers.map((l) => (
                <MenuItem key={l.refId} value={l.refId}>
                  {l.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Currency"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              sx={{ minWidth: 120 }}
            >
              {currencies.map((c) => (
                <MenuItem key={c.refId} value={c.refId}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Refresh
            </Button>
          </Stack>
        </Box>

        <Typography variant="overline" color="text.secondary">
          Active Loan Book by GL month (ZWD)
        </Typography>
        <Box sx={{ width: '100%', height: { xs: 280, md: 380 }, mt: 1 }}>
          <Chart
            key={refreshKey}
            options={options}
            series={series}
            type="area"
            height="100%"
            width="100%"
          />
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle2" color="text.primary" sx={{ mb: 1, fontWeight: 700 }}>
            Monthly breakdown
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.default' }}>
                  <TableCell sx={{ fontWeight: 700 }}>GL Month</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Currency</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    Total Amount
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Result Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dataList.map((row) => (
                  <TableRow key={row.glMonth} hover>
                    <TableCell>{row.glMonth}</TableCell>
                    <TableCell>{row.currency}</TableCell>
                    <TableCell align="right">{formatUSD(row.totalAmount)}</TableCell>
                    <TableCell>{new Date(row.resultDate).toLocaleDateString('en-US')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default LoansZwd;
